<template>
  <div class="bg-white px2-pc px1-mobile pb-1">
    <div class="backlink mt3-pc">
      <a
        layout="row"
        layout-align="start center"
        @click="back"
      >
        <v-icon
          class="mr-1"
          color="grey"
        >mdi-chevron-left</v-icon>{{ isNew ? $t('surveys.back') : $t('surveys.back_sheet')}}
      </a>
    </div>
    <div
      layout="row"
      layout-align="start center"
      class="title mb-2 mt3-pc mt1-mobile ml-1"
    >{{isNew ? $t('surveys.new') : $t('surveys.edit')}}</div>

    <v-form
      v-if="!loading"
      class="mt5-pc mt3-mobile"
      layout="column"
      layout-align="center center"
      ref="form"
      v-model="formValid"
      @submit.prevent="save"
    >
      <div class="width-95-mobile field-width">
        <div
          layout="row"
          layout-align="start center"
          class="title grey--text text-uppercase font-weight-medium mb-2 mt3-pc mt1-mobile"
        >{{$t('surveys.desc')}}</div>

        <div class="font-weight-bold mt-3">{{$t('surveys.langs')}} *</div>
        <v-autocomplete
          v-model="survey.langs_ids"
          :items="$store.state.folangs"
          item-value="id"
          :item-text="'name.' + _locale"
          dense
          :placeholder="$t('surveys.langs')"
          multiple
          chips
          :rules="[ v => (!!v && !!v.length) || $t('errors.required') ]"
          deletable-chips
          :hint="$t('surveys.langs_hint')"
          persistent-hint
          class="full-width"
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-chip
              :selected="data.selected"
              close
              label
              color="primary"
              text-color="white"
              @input="removeLang(data.item)"
            >
              {{ i18n(data.item.name) }} (<span class="text-uppercase">{{data.item.id}}</span>)
            </v-chip>
          </template>
        </v-autocomplete>

        <div
          v-show="survey.langs_ids.length"
          class="font-weight-bold mt-3"
        >{{$t('surveys.title')}} *</div>
        <v-text-field
          v-for="l in survey.langs_ids"
          :key="'title_'+l"
          :label="l.toUpperCase()"
          v-model="survey.title[l]"
          class="field-width"
          :rules="[ v => !!v || $t('errors.required') ]"
        ></v-text-field>

        <div
          v-show="survey.langs_ids.length"
          class="font-weight-bold mt-3"
        >{{$t('surveys.short_desc')}} *</div>
        <v-textarea
          v-for="l in survey.langs_ids"
          :key="'desc_'+l"
          :label="l.toUpperCase()"
          v-model="survey.description[l]"
          class="field-width"
          :rules="[ v => !!v || $t('errors.required') ]"
        ></v-textarea>

        <div
          v-show="survey.langs_ids.length"
          class="font-weight-bold mt-3"
        >{{$t('surveys.thx')}} ({{$t('surveys.thx_details')}}) *</div>
        <v-textarea
          v-for="l in survey.langs_ids"
          :key="'thx_'+l"
          :label="l.toUpperCase()"
          v-model="survey.thanks[l]"
          class="field-width"
          :rules="[ v => !!v || $t('errors.required') ]"
        ></v-textarea>

        <div
          layout="row"
          layout-align="start center"
          class="title grey--text text-uppercase font-weight-medium mb-2 mt3-pc mt1-mobile"
        >{{$t('surveys.publication')}}</div>

        <div
          layout="row"
          layout-align="start center"
          layout-wrap
          class="mt-3"
        >
          <div
            layout="column"
            flex="50"
          >
            <div
              layout="row"
              layout-align="start center"
            >
              <span class="font-weight-bold mr-3">{{$t('_.from')}}</span>
              <date-field
                @input="onStartInput"
                name="from"
                v-model="start"
              />
            </div>
          </div>
          <div
            layout="column"
            flex
          >
            <div
              layout="row"
              layout-align="start center"
            >
              <span class="font-weight-bold mr-3">{{$t('_.to')}}</span>
              <date-field
                name="to"
                @input="onEndInput"
                v-model="end"
              />
            </div>
          </div>
        </div>
        <message-form
          ref="messageForm"
          v-model="survey.message"
        ></message-form>
        <div
          layout="row"
          layout-align="start center"
          class="title grey--text text-uppercase font-weight-medium mb-2 mt3-pc mt1-mobile"
        >{{$t('surveys.target_title')}}</div>
        <div class="font-weight-bold mt-3 mb-2">{{$t('surveys.lv_targeted')}}</div>
        <div
          layout="row"
          layout-align="start center"
          layout-wrap
        >
          <v-checkbox
            v-for="l in levels"
            hide-details
            :label="l.text"
            :value="l.value"
            v-model="survey.message.levels_ids"
            :key="`levelmessage_${l.value}`"
            class="mt-0 pt-0 mr-4"
            style="flex: none"
          ></v-checkbox>
        </div>

        <div class="font-weight-bold mt-3 mb-2">{{$t('surveys.add_filters')}}</div>
        <div
          layout="row"
          layout-wrap
          class="full-width pa-1"
        >
          <div
            layout="column"
            :flex="$vuetify.breakpoint. smAndDown ? 100 : 50"
            class="mt2-pc"
          >
            <v-select
              :label="$t('params.sectors')"
              :hint="$t('filters.sectors_select')"
              persistent-hint
              multiple
              solo
              v-model="survey.message.sectors_ids"
              :items="sectors"
              class="mr5-pc width-100-mobile"
            >
            </v-select>
          </div>
          <div
            layout="column"
            :flex="$vuetify.breakpoint. smAndDown ? 100 : 50"
            class="mt2-pc"
          >
            <v-select
              :label="$t('params.rds')"
              :hint="$t('filters.rds_select')"
              persistent-hint
              multiple
              solo
              v-model="survey.message.regional_directorates_ids"
              :items="rds"
              item-value="id"
              :item-text="itemLabel"
              class="ml5-pc width-100-mobile"
            >
            </v-select>
          </div>
          <div
            layout="column"
            :flex="$vuetify.breakpoint. smAndDown ? 100 : 50"
            class="mt2-pc"
          >
            <v-select
              :label="$t('params.regions')"
              :hint="$t('filters.regions_select')"
              persistent-hint
              multiple
              solo
              v-model="survey.message.regions_ids"
              :items="regions"
              item-value="id"
              :item-text="itemName"
              class="mr5-pc width-100-mobile"
            >
            </v-select>
          </div>
          <div
            layout="column"
            :flex="$vuetify.breakpoint.smAndDown ? 100 : 50"
            class="mt2-pc"
          >
            <v-autocomplete
              v-model="survey.message.cities_ids"
              :items="$store.state.cities"
              item-value="id"
              item-text="label"
              dense
              multiple
              chips
              solo
              single-line
              deletable-chips
              :label="$t('params.cities')"
              :hint="$t('filters.cities_select')"
              persistent-hint
              class="ml5-pc width-100-mobile"
            ></v-autocomplete>
          </div>
          <div
            layout="column"
            :flex="$vuetify.breakpoint.smAndDown ? 100 : 50"
            class="mt2-pc"
          >
            <v-autocomplete
              v-model="survey.message.schools_ids"
              :items="schools"
              item-value="id"
              item-text="name"
              dense
              multiple
              chips
              solo
              single-line
              deletable-chips
              :label="$t('schools.title')"
              :hint="$t('filters.schools_select')"
              persistent-hint
              class="width-100-mobile mr5-pc"
            ></v-autocomplete>
          </div>
        </div>
      </div>
      <div
        layout="row"
        layout-align="end center"
        class="field-width width-95-mobile mt-2 mb-2"
      >
        <v-btn
          depressed
          color="secondary"
          @click="save"
          style="text-transform: none"
        >{{$t('_.save')}}</v-btn>
        &nbsp;{{$t('_.or')}}&nbsp;
        <a
          @click.prevent="back"
          href="#"
          class="grey--text"
        >{{$t('_.cancel')}}</a>
      </div>
    </v-form>

    <div
      layout="row"
      style="height: 100%"
      layout-align="center center"
      v-else
    >
      <v-progress-circular
        indeterminate
        color="secondary"
        size="64"
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import Params from '@/api/params'
import Schools from '@/api/schools'
import Surveys from '@/api/surveys'
import Consts from '@/consts'
import I18nMixin from '@/mixins/i18n'
import DateField from '@/components/shared/DateField'
import MessageForm from '@/components/shared/MessageForm'
import FormDataMixin from '@/mixins/formdata'
import moment from 'moment'

export default {
  mixins: [I18nMixin, FormDataMixin],
  components: { DateField, MessageForm },
  data () {
    return {
      survey: {
        id: null,
        title: {},
        description: {},
        thanks: {},
        langs_ids: [],
        message: {
          body: '',
          date_start: null,
          date_end: null,
          levels_ids: [],
          sectors_ids: [],
          schools_ids: [],
          regional_directorates_ids: [],
          regions_ids: [],
          cities_ids: [],
          type: Consts.SURVEY
        }
      },
      formValid: false,
      schools: [],
      rds: [],
      regions: [],
      loading: false,
      start: '',
      end: ''
    }
  },
  mounted () {
    if (!this.isNew) {
      this.fetch()
    } else {
      this.fetchParams()
    }
  },
  methods: {
    fetch () {
      this.loading = true
      Surveys.get(this.$route.params.id).then(res => {
        this.survey = res.data
        this.start = this.survey.message.date_start
        this.end = this.survey.message.date_end
        this.loading = false
        this.fetchParams()
      })
    },
    fetchParams () {
      Schools.getList({ sortBy: 'name', is_enabled: 1, current_id: this.survey.message.schools_ids.length ? this.survey.message.schools_ids : null }).then(res => {
        this.schools = res.data
      })
      Params.getList('regional-directorates', { is_enabled: 1, current_id: this.survey.message.regional_directorates_ids.length ? this.survey.message.regional_directorates_ids : null }).then(res => {
        this.rds = res.data
        this.rds = this.rds.sort((a, b) => (this.i18n(a.label) > this.i18n(b.label)) ? 1 : ((this.i18n(b.label) > this.i18n(a.label)) ? -1 : 0))
      })
      Params.getList('regions', { is_enabled: 1, current_id: this.survey.message.regions_ids.length ? this.survey.message.regions_ids : null }).then(res => {
        this.regions = res.data
        this.regions = this.regions.sort((a, b) => (this.i18n(a.name) > this.i18n(b.name)) ? 1 : ((this.i18n(b.name) > this.i18n(a.name)) ? -1 : 0))
      })
    },
    save () {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      // Convert the data to FormData for proper file upload
      let formData = new FormData()

      this.survey.message.subject = this.$t('surveys.new')

      let o = Object.assign({}, this.survey, { deleted_medias: this.$refs.messageForm.deletedMedias }, { deleted_docs: this.$refs.messageForm.deletedDocs })
      this._convertObjectToFormData(formData, o)

      for (let i = 0; i < this.$refs.messageForm.medias.length; i++) {
        if (!this.$refs.messageForm.medias[i].id) {
          formData.append("medias[" + i + "]", this.$refs.messageForm.medias[i], this.$refs.messageForm.medias[i].name)
        }
      }

      for (let i = 0; i < this.$refs.messageForm.documents.length; i++) {
        if (!this.$refs.messageForm.documents[i].id) {
          formData.append("documents[" + i + "]", this.$refs.messageForm.documents[i], this.$refs.messageForm.documents[i].name)
        }
      }

      if (this.isNew) {
        Surveys.create(formData).then((res) => {
          this.loading = false
          this.$router.push({ name: 'surveys-show-info', params: { id: res.data.id } })
          this.$snotify.success(this.$t('surveys.created'))
        }, () => {
          this.loading = false
        })
      } else {
        Surveys.updateAsForm(this.survey.id, formData).then(() => {
          this.loading = false
          this.$router.push({ name: 'surveys-show-info', params: { id: this.survey.id } })
          this.$snotify.success(this.$t('surveys.edited'))
        }, () => {
          this.loading = false
        })
      }
    },
    back () {
      if (this.isNew) {
        this.$router.push({ name: 'surveys-index' })
      } else {
        this.$router.push({ name: 'surveys-show-info', params: { id: this.survey.id } })
      }
    },
    itemName (item) {
      return this.i18n(item.name)
    },
    itemLabel (item) {
      return this.i18n(item.label)
    },
    removeLang (item) {
      const index = this.survey.langs_ids.indexOf(item.id)
      if (index >= 0) this.survey.langs_ids.splice(index, 1)
    },
    parseDate (date) {
      if (!date) return null

      if (date.indexOf('-') >= 0) {
        return date
      }

      return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
    },
    onStartInput (val) {
      this.survey.message.date_start = val
    },
    onEndInput (val) {
      this.survey.message.date_end = val
    },
  },
  computed: {
    isNew () {
      return this.$route.params.id === 'new'
    },
    sectors () {
      return [
        { value: Consts.PRIMARY, text: this.$t('schools.sector_prim') },
        { value: Consts.SECONDARY, text: this.$t('schools.sector_sec') },
        { value: Consts.COMPETENCECENTRE, text: this.$t('schools.sector_comp') },
      ]
    },
    levels () {
      return [
        { value: Consts.NATIONAL, text: this.$t('users.lv_nat') },
        { value: Consts.SECTORAL, text: this.$t('users.lv_sec') },
        { value: Consts.LOCAL, text: this.$t('users.lv_loc') },
      ]
    }
  }
}
</script>